import React, { useEffect, useState } from 'react'
import {
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  Divider,
  Stack,
} from '@mui/material'
import Roles from './MandatoryTrainingPopupRoles'
import API from '../../../services/API'
import BigModal from '../../../components/Modals/BigModal'
import CatalogueCourseSettings, {
  refresherError,
} from '../../../training/components/admin/CatalogueCourseSettings'
import {
  catalogueCourseFor,
  lowestPathway,
  normaliseRequiredBy,
} from '../../../training/components/admin/catalogueHelpers'
import {
  errorMessage,
  isOk,
  updateCourseLink,
} from '../../../training/helpers/trainingApi'

const emptyForm = { title: '', frequency: 'Annually', requiredBy: {} }
const emptyELearning = {
  catalogueCode: '',
  expiryOverrideMonths: '',
  testOutAllowed: false,
}

const eLearningOf = (course) => ({
  catalogueCode: course?.catalogueCode ?? '',
  expiryOverrideMonths: course?.expiryOverrideMonths ?? '',
  testOutAllowed: !!course?.testOutAllowed,
})

// Add or edit one of the organisation's courses: name, frequency and which
// roles need it. With e-learning available (Premium and above) an existing
// course can also be linked to a catalogue course, which adds the e-learning
// options and, for courses with several pathways, a pathway per role.
const AddCoursePopup = ({
  addCoursePopupOpen,
  setAddCoursePopupOpen,
  selectedCourse,
  setSelectedCourse,
  roles = [],
  catalogue,
  reload,
}) => {
  const [form, setForm] = useState(emptyForm)
  const [eLearning, setELearning] = useState(emptyELearning)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const available = !!catalogue?.available
  const catalogueCourses = catalogue?.courses ?? []
  const linkedCourse = available
    ? catalogueCourseFor(catalogueCourses, eLearning.catalogueCode)
    : null
  const pathways = linkedCourse?.pathways ?? []
  // Still linked on the server, whatever the form now says
  const savedLink = available && !!selectedCourse?.catalogueCode

  useEffect(() => {
    if (!addCoursePopupOpen) return
    const saved = eLearningOf(selectedCourse)
    const savedPathways = available
      ? (catalogueCourseFor(catalogueCourses, saved.catalogueCode)?.pathways ??
        [])
      : []
    setELearning(saved)
    setForm({
      title: selectedCourse?.title ?? '',
      frequency: selectedCourse?.frequency ?? 'Annually',
      // Below Premium the stored values are left exactly as they are
      requiredBy: available
        ? normaliseRequiredBy(selectedCourse?.requiredBy, roles, savedPathways)
        : { ...(selectedCourse?.requiredBy ?? {}) },
    })
    setError(null)
  }, [addCoursePopupOpen, selectedCourse, roles, catalogue])

  const close = () => {
    setAddCoursePopupOpen(false)
    setSelectedCourse(null)
  }

  // Changing the link changes what a role's value can be (tick or pathway)
  const handleELearningChange = (next) => {
    if (next.catalogueCode !== eLearning.catalogueCode) {
      const nextPathways =
        catalogueCourseFor(catalogueCourses, next.catalogueCode)?.pathways ?? []
      setForm((prev) => ({
        ...prev,
        requiredBy: normaliseRequiredBy(prev.requiredBy, roles, nextPathways),
      }))
      next = { ...next, expiryOverrideMonths: '' }
    }
    setELearning(next)
  }

  const handleDeleteCourse = async () => {
    setSaving(true)
    const res = await API.post(
      '/admin/siteconfig/mandatory-training-delete-course',
      { courseID: selectedCourse._id },
    )
    setSaving(false)
    if (res.status === 200) {
      close()
      reload()
    } else {
      setError(errorMessage(res, 'Unable to delete this course.'))
    }
  }

  // Only what changed goes to the e-learning route. Returns an error or null.
  const saveELearning = async () => {
    const before = eLearningOf(selectedCourse)
    const code = eLearning.catalogueCode || null
    const body = {}
    if (code !== (before.catalogueCode || null)) body.catalogueCode = code
    if (code) {
      const months =
        eLearning.expiryOverrideMonths === ''
          ? null
          : Number(eLearning.expiryOverrideMonths)
      const monthsBefore =
        before.expiryOverrideMonths === '' || 'catalogueCode' in body
          ? null
          : Number(before.expiryOverrideMonths)
      if (months !== monthsBefore || 'catalogueCode' in body) {
        body.expiryOverrideMonths = months
      }
      if (eLearning.testOutAllowed !== before.testOutAllowed) {
        body.testOutAllowed = eLearning.testOutAllowed
      }
    } else if (before.catalogueCode) {
      body.expiryOverrideMonths = null
      body.testOutAllowed = false
    }
    if (Object.keys(body).length === 0) return null
    const res = await updateCourseLink(selectedCourse._id, body)
    return isOk(res)
      ? null
      : errorMessage(res, 'Unable to save the e-learning settings.')
  }

  const addCourseSubmit = async (e) => {
    e.preventDefault()
    setError(null)
    if (linkedCourse && refresherError(eLearning.expiryOverrideMonths, linkedCourse)) {
      return
    }
    setSaving(true)
    try {
      if (selectedCourse) {
        if (available) {
          const eLearningError = await saveELearning()
          if (eLearningError) {
            setError(eLearningError)
            return
          }
        }
        const res = await API.put('/admin/siteconfig/mandatory-training', {
          courseID: selectedCourse._id,
          ...form,
        })
        if (res.status !== 200) {
          setError(errorMessage(res, 'Unable to save this course.'))
          return
        }
      } else {
        const res = await API.post('/admin/siteconfig/mandatory-training', form)
        if (res.status !== 200) {
          setError(
            res.data?.errorCode === 'duplicate'
              ? 'This course already exists'
              : errorMessage(res, 'Unable to add this course.'),
          )
          return
        }
      }
      reload()
      close()
    } finally {
      setSaving(false)
    }
  }

  return (
    <BigModal
      open={!!addCoursePopupOpen}
      onClose={close}
      autoWidth
      style={{ width: { xs: 'calc(100vw - 24px)', sm: 520 }, padding: 3 }}
    >
      <Stack gap={2} component="form" onSubmit={addCourseSubmit}>
        <Typography variant="title" textAlign="center">
          {selectedCourse
            ? `Edit ${selectedCourse?.title}`
            : 'Add a Mandatory Training Course'}
        </Typography>
        <TextField
          label="Course Name"
          required
          autoFocus={!selectedCourse}
          fullWidth
          value={form.title}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, title: e.target.value }))
          }
        />
        <FormControl fullWidth>
          <InputLabel id="frequency-label">Frequency</InputLabel>
          <Select
            labelId="frequency-label"
            id="frequency"
            value={form.frequency ?? ''}
            label="Frequency"
            onChange={(event) =>
              setForm((prev) => ({ ...prev, frequency: event.target.value }))
            }
          >
            <MenuItem value="Annually">Annually</MenuItem>
            <MenuItem value="Two Years">2 years</MenuItem>
            <MenuItem value="Three Years">3 years</MenuItem>
          </Select>
        </FormControl>
        {linkedCourse && (
          <Typography variant="body2" color="text.secondary">
            The frequency applies to uploaded certificates and records added by
            hand. E-learning completions use the refresher period below.
          </Typography>
        )}

        {available && selectedCourse && (
          <>
            <Divider />
            <Typography variant="title3">E-learning</Typography>
            <CatalogueCourseSettings
              catalogueCourses={catalogueCourses}
              courseId={selectedCourse._id}
              value={eLearning}
              onChange={handleELearningChange}
              disabled={saving}
            />
          </>
        )}

        <Divider />
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="title3">Required for</Typography>
          <Button
            onClick={() =>
              setForm((prev) => ({
                ...prev,
                requiredBy: {
                  ...prev.requiredBy,
                  ...Object.fromEntries(
                    roles.map((role) => [
                      role,
                      pathways.length > 1 ? lowestPathway(pathways).id : true,
                    ]),
                  ),
                },
              }))
            }
            color="contrast"
          >
            Select All
          </Button>
        </Stack>
        {pathways.length > 1 && (
          <Typography variant="body2" color="text.secondary">
            This e-learning course has more than one pathway. Choose the one
            each role must complete. A higher pathway also covers the lower
            ones.
          </Typography>
        )}
        <Roles
          setAddCourseFormValues={setForm}
          addCourseFormValues={form}
          roles={roles}
          pathways={pathways}
        />

        {error && <Alert severity="error">{error}</Alert>}

        {selectedCourse &&
          (savedLink ? (
            <Alert severity="info">
              This course is linked to an e-learning course, so it cannot be
              deleted. To delete it, choose "Not linked" above and save first.
            </Alert>
          ) : (
            <Button
              type="button"
              variant="contained"
              onClick={handleDeleteCourse}
              color="error"
              disabled={saving}
            >
              Delete Course
            </Button>
          ))}
        <Button
          type="submit"
          variant="contained"
          color="third"
          sx={{ color: 'white' }}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Submit'}
        </Button>
      </Stack>
    </BigModal>
  )
}

export default AddCoursePopup
